import * as React from 'react'
import { cn } from '../../lib/utils'

/**
 * Radio — native `<input type="radio">` styled to the Figma DS2026 "Radio" spec
 * (First Bite Design System, node 1517:34684). Checked = basil-700 fill + a
 * white center dot; brand 4px focus ring; disabled = gray fill + gray dot.
 *
 *   size    sm | md          (16px / 20px)
 *   status  default | error
 *
 * Group radios with `RadioGroup` for value management (and native arrow-key
 * navigation between options). Pass `label` for an inline clickable label.
 */

type RadioSize = 'sm' | 'md'
type RadioStatus = 'default' | 'error'

interface RadioGroupContextValue {
  name: string
  value?: string
  onValueChange?: (value: string) => void
  size: RadioSize
  status: RadioStatus
  disabled?: boolean
}

const RadioGroupContext = React.createContext<RadioGroupContextValue | null>(
  null
)

const boxSize: Record<RadioSize, string> = {
  sm: 'size-4',
  md: 'size-5',
}

const dotSize: Record<RadioSize, string> = {
  sm: 'size-1.5',
  md: 'size-2',
}

const statusBox: Record<RadioStatus, string> = {
  default: cn(
    'border-fbui-gray-300 enabled:hover:border-fbui-gray-400',
    'checked:border-fbui-basil-700 checked:bg-fbui-basil-700 enabled:checked:hover:border-fbui-basil-800 enabled:checked:hover:bg-fbui-basil-800',
    'focus-visible:ring-fbui-ring-brand'
  ),
  error:
    'border-fbui-red-500 enabled:hover:border-fbui-red-600 checked:border-fbui-red-500 checked:bg-fbui-red-500 focus-visible:ring-fbui-ring-error',
}

export interface RadioProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'size' | 'type'
> {
  size?: RadioSize
  status?: RadioStatus
  /** Optional inline label, rendered in a clickable `<label>` wrapper. */
  label?: React.ReactNode
  /** Classes on the `<label>`/wrapper. */
  wrapperClassName?: string
}

export const Radio = React.forwardRef<HTMLInputElement, RadioProps>(
  (
    {
      className,
      wrapperClassName,
      size,
      status,
      label,
      disabled,
      name,
      checked,
      value,
      onChange,
      ...props
    },
    ref
  ) => {
    const group = React.useContext(RadioGroupContext)
    const resolvedSize = size ?? group?.size ?? 'md'
    const resolvedStatus = status ?? group?.status ?? 'default'
    const isDisabled = disabled ?? group?.disabled
    const resolvedName = name ?? group?.name
    const resolvedChecked =
      checked ?? (group && value != null ? group.value === value : undefined)

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(e)
      if (group && value != null) group.onValueChange?.(String(value))
    }

    const control = (
      <span className="relative inline-flex shrink-0">
        <input
          ref={ref}
          type="radio"
          name={resolvedName}
          value={value}
          checked={resolvedChecked}
          disabled={isDisabled}
          onChange={handleChange}
          className={cn(
            'peer shrink-0 appearance-none rounded-full border bg-fbui-base-white outline-none transition focus-visible:ring-2',
            'disabled:cursor-not-allowed disabled:border-fbui-gray-300 disabled:bg-fbui-gray-100',
            'disabled:checked:border-fbui-gray-300 disabled:checked:bg-fbui-gray-100',
            boxSize[resolvedSize],
            statusBox[resolvedStatus],
            className
          )}
          {...props}
        />
        <span
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute inset-0 m-auto rounded-full bg-fbui-base-white opacity-0 peer-checked:opacity-100 peer-disabled:bg-fbui-text-disabled',
            dotSize[resolvedSize]
          )}
        />
      </span>
    )

    if (!label) {
      return wrapperClassName ? (
        <span className={cn('inline-flex', wrapperClassName)}>{control}</span>
      ) : (
        control
      )
    }

    return (
      <label
        className={cn(
          'inline-flex items-center gap-2.5 tracking-[-0.2px] select-none',
          resolvedSize === 'sm' ? 'text-xs' : 'text-sm',
          isDisabled
            ? 'cursor-not-allowed text-fbui-text-disabled'
            : 'cursor-pointer text-fbui-gray-700',
          wrapperClassName
        )}
      >
        {control}
        <span>{label}</span>
      </label>
    )
  }
)
Radio.displayName = 'Radio'

export interface RadioGroupProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'onChange'
> {
  /** Shared input `name`. Auto-generated when omitted. */
  name?: string
  /** Selected value (controlled). */
  value?: string
  /** Initial selected value (uncontrolled). */
  defaultValue?: string
  onValueChange?: (value: string) => void
  size?: RadioSize
  status?: RadioStatus
  disabled?: boolean
}

/**
 * Groups `Radio`s: shares a `name`, manages the selected value (controlled or
 * uncontrolled), and publishes size/status/disabled. `role="radiogroup"` for
 * assistive tech; native radios handle arrow-key navigation.
 */
export const RadioGroup = React.forwardRef<HTMLDivElement, RadioGroupProps>(
  (
    {
      className,
      name,
      value,
      defaultValue,
      onValueChange,
      size = 'md',
      status = 'default',
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const generatedName = React.useId()
    const isControlled = value !== undefined
    const [internalValue, setInternalValue] = React.useState(defaultValue)
    const currentValue = isControlled ? value : internalValue

    const handleValueChange = React.useCallback(
      (next: string) => {
        if (!isControlled) setInternalValue(next)
        onValueChange?.(next)
      },
      [isControlled, onValueChange]
    )

    const ctx = React.useMemo<RadioGroupContextValue>(
      () => ({
        name: name ?? generatedName,
        value: currentValue,
        onValueChange: handleValueChange,
        size,
        status,
        disabled,
      }),
      [
        name,
        generatedName,
        currentValue,
        handleValueChange,
        size,
        status,
        disabled,
      ]
    )

    return (
      <RadioGroupContext.Provider value={ctx}>
        <div
          ref={ref}
          role="radiogroup"
          className={cn('flex flex-col gap-2.5', className)}
          {...props}
        >
          {children}
        </div>
      </RadioGroupContext.Provider>
    )
  }
)
RadioGroup.displayName = 'RadioGroup'
